import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    Handle,
    Position,
    MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { Card, Button, Space, Spin, message, Tag, Tooltip, Modal, Descriptions, Badge } from 'antd';
import {
    ReloadOutlined,
    CloudServerOutlined,
    ClusterOutlined,
    DesktopOutlined,
    ExpandOutlined,
    CompressOutlined,
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

// Dagre布局配置
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
    const isHorizontal = direction === 'LR';
    dagreGraph.setGraph({ rankdir: direction, nodesep: 80, ranksep: 100 });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: node.width || 180, height: node.height || 60 });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - (node.width || 180) / 2,
                y: nodeWithPosition.y - (node.height || 60) / 2,
            },
        };
    });

    return { nodes: layoutedNodes, edges };
};

// 自定义节点: Zone
const ZoneNode = ({ data }) => {
    return (
        <div
            style={{
                padding: '12px 16px',
                borderRadius: 8,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                minWidth: 160,
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            }}
        >
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ClusterOutlined style={{ fontSize: 18 }} />
                <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{data.label}</div>
                    <div style={{ fontSize: 11, opacity: 0.9 }}>{data.code}</div>
                </div>
            </div>
            {data.nodeCount !== undefined && (
                <div style={{ marginTop: 8, fontSize: 11, opacity: 0.8 }}>
                    {data.nodeCount} 节点
                </div>
            )}
        </div>
    );
};

// 自定义节点: Node (计算节点)
const HostNode = ({ data }) => {
    const statusColor = data.status === 'enable' ? '#52c41a' : '#ff4d4f';
    return (
        <div
            style={{
                padding: '12px 16px',
                borderRadius: 8,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                minWidth: 160,
                boxShadow: '0 4px 12px rgba(17, 153, 142, 0.4)',
                cursor: 'pointer',
            }}
            onClick={() => data.onClick && data.onClick(data.raw)}
        >
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CloudServerOutlined style={{ fontSize: 18 }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{data.label}</div>
                    <div style={{ fontSize: 11, opacity: 0.9 }}>{data.ip}</div>
                </div>
                <div
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: statusColor,
                    }}
                />
            </div>
            {data.vmCount !== undefined && (
                <div style={{ marginTop: 8, fontSize: 11, opacity: 0.8 }}>
                    {data.vmCount} 虚拟机
                </div>
            )}
        </div>
    );
};

// 自定义节点: VM
const VmNode = ({ data }) => {
    const statusColors = {
        active: '#52c41a',
        suspend: '#faad14',
        create: '#1890ff',
        delete: '#ff4d4f',
        rebuild: '#722ed1',
        migrate: '#13c2c2',
        flow_limit: '#fa541c',
    };
    const statusColor = statusColors[data.status] || '#d9d9d9';

    return (
        <div
            style={{
                padding: '10px 14px',
                borderRadius: 6,
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                color: 'white',
                minWidth: 140,
                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.4)',
                cursor: 'pointer',
            }}
            onClick={() => data.onClick && data.onClick(data.raw)}
        >
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <DesktopOutlined style={{ fontSize: 16 }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{data.label}</div>
                    <div style={{ fontSize: 10, opacity: 0.9 }}>{data.ip || 'No IP'}</div>
                </div>
                <Tooltip title={data.status}>
                    <div
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: statusColor,
                        }}
                    />
                </Tooltip>
            </div>
        </div>
    );
};

const nodeTypes = {
    zone: ZoneNode,
    host: HostNode,
    vm: VmNode,
};

const Topology = () => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [loading, setLoading] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);

    // 原始数据
    const [rawData, setRawData] = useState({ zones: [], nodes: [], vms: [] });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [zonesRes, nodesRes, vmsRes] = await Promise.all([
                evmAdminService.getZoneList(),
                evmAdminService.getNodeList(),
                evmAdminService.getVms(),
            ]);

            const zones = zonesRes.status === 'success' ? zonesRes.data?.zones || [] : [];
            const nodeList = nodesRes.status === 'success' ? nodesRes.data?.nodes || [] : [];
            const vmList = vmsRes.status === 'success' ? vmsRes.data || [] : [];

            setRawData({ zones, nodes: nodeList, vms: vmList });

            // 构建节点和边
            const flowNodes = [];
            const flowEdges = [];

            // 按zone分组nodes
            const nodesByZone = {};
            nodeList.forEach((node) => {
                const zoneId = node.zone_id || 'unassigned';
                if (!nodesByZone[zoneId]) nodesByZone[zoneId] = [];
                nodesByZone[zoneId].push(node);
            });

            // 按node分组vms
            const vmsByNode = {};
            vmList.forEach((vm) => {
                const nodeId = vm.node_id || 'unassigned';
                if (!vmsByNode[nodeId]) vmsByNode[nodeId] = [];
                vmsByNode[nodeId].push(vm);
            });

            // 创建Zone节点
            zones.forEach((zone) => {
                const zoneNodeId = `zone-${zone.id}`;
                flowNodes.push({
                    id: zoneNodeId,
                    type: 'zone',
                    data: {
                        label: zone.name,
                        code: zone.code,
                        nodeCount: (nodesByZone[zone.id] || []).length,
                        raw: zone,
                    },
                    position: { x: 0, y: 0 },
                    width: 180,
                    height: 70,
                });

                // 创建该Zone下的Node节点
                const zoneNodes = nodesByZone[zone.id] || [];
                zoneNodes.forEach((node) => {
                    const hostNodeId = `node-${node.id}`;
                    flowNodes.push({
                        id: hostNodeId,
                        type: 'host',
                        data: {
                            label: node.name,
                            ip: node.ipaddress,
                            status: node.status,
                            vmCount: (vmsByNode[node.id] || []).length,
                            raw: node,
                            onClick: handleNodeClick,
                        },
                        position: { x: 0, y: 0 },
                        width: 180,
                        height: 70,
                    });

                    // Zone -> Node 边
                    flowEdges.push({
                        id: `edge-${zoneNodeId}-${hostNodeId}`,
                        source: zoneNodeId,
                        target: hostNodeId,
                        type: 'smoothstep',
                        animated: false,
                        style: { stroke: '#667eea', strokeWidth: 2 },
                        markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' },
                    });

                    // 创建该Node下的VM节点
                    const nodeVms = vmsByNode[node.id] || [];
                    nodeVms.forEach((vm) => {
                        const vmNodeId = `vm-${vm.id}`;
                        flowNodes.push({
                            id: vmNodeId,
                            type: 'vm',
                            data: {
                                label: vm.name,
                                ip: vm.ip_address,
                                status: vm.status,
                                raw: vm,
                                onClick: handleVmClick,
                            },
                            position: { x: 0, y: 0 },
                            width: 150,
                            height: 50,
                        });

                        // Node -> VM 边
                        flowEdges.push({
                            id: `edge-${hostNodeId}-${vmNodeId}`,
                            source: hostNodeId,
                            target: vmNodeId,
                            type: 'smoothstep',
                            animated: false,
                            style: { stroke: '#11998e', strokeWidth: 1.5 },
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#11998e' },
                        });
                    });
                });
            });

            // 处理未分配Zone的节点
            const unassignedNodes = nodesByZone['unassigned'] || [];
            if (unassignedNodes.length > 0) {
                const unassignedZoneId = 'zone-unassigned';
                flowNodes.push({
                    id: unassignedZoneId,
                    type: 'zone',
                    data: {
                        label: '未分配',
                        code: 'unassigned',
                        nodeCount: unassignedNodes.length,
                    },
                    position: { x: 0, y: 0 },
                    width: 180,
                    height: 70,
                });

                unassignedNodes.forEach((node) => {
                    const hostNodeId = `node-${node.id}`;
                    flowNodes.push({
                        id: hostNodeId,
                        type: 'host',
                        data: {
                            label: node.name,
                            ip: node.ipaddress,
                            status: node.status,
                            vmCount: (vmsByNode[node.id] || []).length,
                            raw: node,
                            onClick: handleNodeClick,
                        },
                        position: { x: 0, y: 0 },
                        width: 180,
                        height: 70,
                    });

                    flowEdges.push({
                        id: `edge-${unassignedZoneId}-${hostNodeId}`,
                        source: unassignedZoneId,
                        target: hostNodeId,
                        type: 'smoothstep',
                        animated: false,
                        style: { stroke: '#667eea', strokeWidth: 2 },
                        markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' },
                    });

                    const nodeVms = vmsByNode[node.id] || [];
                    nodeVms.forEach((vm) => {
                        const vmNodeId = `vm-${vm.id}`;
                        flowNodes.push({
                            id: vmNodeId,
                            type: 'vm',
                            data: {
                                label: vm.name,
                                ip: vm.ip_address,
                                status: vm.status,
                                raw: vm,
                                onClick: handleVmClick,
                            },
                            position: { x: 0, y: 0 },
                            width: 150,
                            height: 50,
                        });

                        flowEdges.push({
                            id: `edge-${hostNodeId}-${vmNodeId}`,
                            source: hostNodeId,
                            target: vmNodeId,
                            type: 'smoothstep',
                            animated: false,
                            style: { stroke: '#11998e', strokeWidth: 1.5 },
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#11998e' },
                        });
                    });
                });
            }

            // 应用布局
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
                flowNodes,
                flowEdges,
                'TB'
            );

            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
        } catch (error) {
            message.error('加载数据失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [setNodes, setEdges]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleNodeClick = (nodeData) => {
        setSelectedNode({ type: 'node', data: nodeData });
        setDetailModalVisible(true);
    };

    const handleVmClick = (vmData) => {
        setSelectedNode({ type: 'vm', data: vmData });
        setDetailModalVisible(true);
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    const containerStyle = fullscreen
        ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              background: '#fff',
          }
        : { height: 'calc(100vh - 200px)', minHeight: 500 };

    return (
        <div>
            <Card
                title={
                    <Space>
                        <ClusterOutlined />
                        <span>EVM 拓扑视图</span>
                        <Tag color="blue">{rawData.zones.length} Zones</Tag>
                        <Tag color="green">{rawData.nodes.length} Nodes</Tag>
                        <Tag color="purple">{rawData.vms.length} VMs</Tag>
                    </Space>
                }
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                            刷新
                        </Button>
                        <Button
                            icon={fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
                            onClick={toggleFullscreen}
                        >
                            {fullscreen ? '退出全屏' : '全屏'}
                        </Button>
                    </Space>
                }
                bodyStyle={{ padding: 0 }}
            >
                <div style={containerStyle}>
                    {loading ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <Spin size="large" tip="加载拓扑数据..." />
                        </div>
                    ) : (
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            nodeTypes={nodeTypes}
                            fitView
                            attributionPosition="bottom-left"
                        >
                            <Background color="#aaa" gap={16} />
                            <Controls />
                            <MiniMap
                                nodeColor={(node) => {
                                    switch (node.type) {
                                        case 'zone':
                                            return '#667eea';
                                        case 'host':
                                            return '#11998e';
                                        case 'vm':
                                            return '#a855f7';
                                        default:
                                            return '#eee';
                                    }
                                }}
                                maskColor="rgba(0, 0, 0, 0.1)"
                            />
                        </ReactFlow>
                    )}
                </div>
            </Card>

            {/* 详情弹窗 */}
            <Modal
                title={
                    selectedNode?.type === 'node'
                        ? `节点详情: ${selectedNode?.data?.name}`
                        : `虚拟机详情: ${selectedNode?.data?.name}`
                }
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={600}
            >
                {selectedNode?.type === 'node' && selectedNode?.data && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="ID">{selectedNode.data.id}</Descriptions.Item>
                        <Descriptions.Item label="名称">{selectedNode.data.name}</Descriptions.Item>
                        <Descriptions.Item label="IP地址">
                            {selectedNode.data.ipaddress}
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                            <Badge
                                status={
                                    selectedNode.data.status === 'enable' ? 'success' : 'error'
                                }
                                text={selectedNode.data.status === 'enable' ? '启用' : '禁用'}
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="Zone ID">
                            {selectedNode.data.zone_id || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="网络类型">
                            {selectedNode.data.network_type || '-'}
                        </Descriptions.Item>
                    </Descriptions>
                )}
                {selectedNode?.type === 'vm' && selectedNode?.data && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="ID">{selectedNode.data.id}</Descriptions.Item>
                        <Descriptions.Item label="名称">{selectedNode.data.name}</Descriptions.Item>
                        <Descriptions.Item label="IP地址">
                            {selectedNode.data.ip_address || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                            <Tag
                                color={
                                    selectedNode.data.status === 'active'
                                        ? 'success'
                                        : selectedNode.data.status === 'suspend'
                                        ? 'warning'
                                        : 'default'
                                }
                            >
                                {selectedNode.data.status}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="CPU">
                            {selectedNode.data.cpu_core || '-'} 核
                        </Descriptions.Item>
                        <Descriptions.Item label="内存">
                            {selectedNode.data.memory ? `${selectedNode.data.memory} MB` : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="磁盘">
                            {selectedNode.data.disk_size ? `${selectedNode.data.disk_size} GB` : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="节点ID">
                            {selectedNode.data.node_id || '-'}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </div>
    );
};

export default Topology;
